<script setup lang="ts">
import type { NewsItem, NewsType } from "../types/announcement";

// Propsの型定義
const props = defineProps<{
	items: NewsItem[];
	isHomePage?: boolean; // ホームページかどうかを判定するフラグ
}>();

// Emitsの定義
const emit = defineEmits<{
	itemClick: [item: NewsItem];
}>();

// ホームページ用のフィルタリング処理
const filteredItems = computed(() => {
    if (!props.isHomePage) {
        return props.items; // ホームページ以外は全て表示
    }

    // 最大5件まで表示
    return props.items.slice(0, 5);
});

// 通知アイテムクリック処理
const handleClick = (item: NewsItem) => {
	// PDFの場合は新しいタブで開く
	if (item.type === "pdf" && item.url) {
		window.open(item.url, "_blank");
	}
	// リンクの場合
	else if (item.type === "link" && item.url) {
		// 内部リンク（/で始まる）の場合はタブ内遷移
		if (item.url.startsWith("/")) {
			navigateTo(item.url);
		}
		// 外部リンクの場合は新しいタブで開く
		else {
			window.open(item.url, "_blank");
		}
	}
	// 親コンポーネントにクリックイベントを送信
	emit("itemClick", item);
};

// アイコン名の型定義を追加
type IconName = "arrow-top-right-on-square" | "chevron-right";

// 通知タイプに応じたアイコン名を取得
const getIconName = (item: NewsItem): IconName | null => {
	switch (item.type) {
		case "pdf":
			return "arrow-top-right-on-square"; // PDF展開アイコン
		case "link":
			// 内部リンク：ページ遷移アイコン、外部リンク：新規タブアイコン
			return item.url?.startsWith("/") ? "chevron-right" : "arrow-top-right-on-square";
		default:
			return null; // テキストのみの場合はアイコンなし
	}
};

// クリック可能かどうかを判定
const isClickable = (type: NewsType) => {
	return type === "pdf" || type === "link";
};
</script>

<template>
    <!-- 通知コンテナ -->
    <div class="news">
        <!-- フィルタリングされたアイテムのループ表示 -->
        <div v-for="(item, index) in filteredItems" :key="index" class="news_item"
            :class="{ 'news_item-clickable': isClickable(item.type) }" @click="handleClick(item)">
            <!-- 日付表示エリア -->
            <div class="news_date">
                {{ item.time }}
            </div>

            <!-- コンテンツエリア -->
            <div class="news_content">
                <!-- 通知内容テキスト -->
                <div class="news_text">
                    {{ item.content }}
                </div>

                <!-- アイコン（PDFまたはリンクの場合のみ表示） -->
                <div v-if="getIconName(item)" class="news_icon">
                    <Icons :name="getIconName(item)!" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
// 通知コンテナのスタイル
.news {
    width: min(91vw, 64.5rem);
    display: flex;
    margin: 1.5rem auto;
    padding: 1rem 0;
    flex-direction: column;
    border-top: 0.0625rem solid #E5E5E7;
    gap: 2rem;
}

// 通知アイテムのスタイル
.news_item {
    position: relative;

    // アイテム間の境界線を表示（最初のアイテム以外）
    &::after {
        content: '';
        position: absolute;
        bottom: -1rem; // gap(2rem)の中央に配置
        left: 0;
        width: 100%;
        border-bottom: 0.0625rem solid #E5E5E7;
    }

    // クリック可能なアイテムのみにカーソルとホバー効果を適用
    &-clickable {
        cursor: pointer;

        &:hover {
            opacity: 0.48;
            transition: opacity 0.3s ease;
        }
    }
}

// 日付表示のスタイル
.news_date {
    font-size: 0.875rem; // 14px
    font-weight: 700;
    color: #4896DC;
    text-align: left;
    line-height: 150%;
    margin-bottom: 0.25rem; // 4px
}

// コンテンツエリアのスタイル
.news_content {
    width: 100%;
    display: flex;
}

// 通知内容テキストのスタイル
.news_text {
    flex: 1;
    font-size: 1rem; // 14px
    font-weight: 400;
    color: #252522;
    line-height: 150%;
    text-align: left;
    transition: opacity 0.3s ease;
    word-break: break-word;
}

// クリック可能なアイテムのテキストのみに下線を表示
.news_item-clickable .news_text {
    text-decoration: underline;
}

// アイコンのスタイル
.news_icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border: 0.0625rem solid #2174C0;
    border-radius: 50%;
    margin: auto 0 auto 1rem;

    .svg-manager {
        width: 1rem;
        height: 1rem;
    }
}

// PC用のスタイル
@include mixin.media(pc) {
    .news {
        margin: 2.25rem auto 3.75rem auto;
        padding: 0 0;
        border-top: none;
    }

    // PC版：日付とコンテンツを並排表示
    .news_item {
        display: flex;
        align-items: flex-start;
        gap: 2.5rem; // 日付とコンテンツ間のギャップ
    }

    .news_date {
        margin: auto;
        font-size: 1rem;
    }
}
</style>
